import Button from '@/common/components/atom/button/button';
import Skeleton from '@/common/components/atom/skeleton/skeleton';
import Modal from '@/common/components/atom/modal/modal';
import Text from '@/common/components/atom/text/text';
import useResponsive from '@/common/hooks/useResponsive';
import useWebView from '@/common/hooks/useWebView';
import useIsNewAppShell from '@/common/hooks/useIsNewAppShell';
import useModal from '@/common/hooks/useModal';
import { CENTERS } from '@/common/types/centers';
import { substituteCity } from '@/common/utils/substituteInPersonLine';
import classNames from '@/common/utils/classNames';
import { isNativeWebView } from '@/common/utils/isNativeWebView';
import scrollIntoViewWithOffset from '@/common/utils/scrollIntoViewWithOffset';
import { useFeatureIsOn, useGrowthBook } from '@growthbook/growthbook-react';
import dynamic from 'next/dynamic';
import { useRouter } from 'next/router';
import queryStirng from 'querystring';
import { useInView } from 'react-intersection-observer';
import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import BulkService from './bulk';
import OnlineVisitSuggestionBanner from './onlineVisitSuggestionBanner';
import { getLocalPrivateSpecialistsConfig } from '@/modules/samanBooking/localPrivateSpecialists/config';
import { LocalPrivateSpecialistSuggestion } from '@/modules/samanBooking/localPrivateSpecialists/LocalPrivateSpecialists';
import { useSamanDoctorAvailabilityStatus } from '@/common/apis/services/booking/samanDoctorAvailabilityStatus';
import { bookRequestAvailability } from '@/common/apis/services/booking/bookRequestAvailability';
import moment from 'jalali-moment';
import { splunkInstance } from '@/common/services/splunk';
import useCustomize from '@/common/hooks/useCustomize';
import Presence from './presence';
import PlasmicServicesWrapper from '.plasmic/Services';
import { Fragment2 } from '@/common/fragment/fragment2';
import {
  formatSamanAvailabilityTime,
  getScopedInPersonAvailability,
  getScopedOnlineVisitAvailability,
  mapSamanAvailabilityToCenters,
} from '@/modules/booking/functions/mapSamanAvailabilityToCenters';
import {
  filterBookableMappedCenterServices,
  mapDoctorCenterServices,
} from '@/modules/booking/functions/mapDoctorCenterServices';
import { useDoctorCenterServices } from '@/common/apis/services/doctor/getDoctorCenterServices';
import { getProfileOnlineVisitVisibility } from '@/modules/profile/utils/onlineVisitVisibility';
import { shouldShowRismanExternalBooking } from '@/modules/profile/utils/rismanBookingVisibility';
import { useChatLifecycleAutoCloseHours } from '@/common/apis/services/hami/chatLifecycleSettings';
import { sendDoctorProfileEvent } from '@/modules/profile/events/nightBatEvents';
import {
  BookingHintsContext,
  BookingSkeleton,
  bookingSkeletonHeight,
  type BookingHints,
} from '@/modules/profile/components/ProfileSectionSkeleton';
import { isOnForSlug } from '@/common/growthbook/isOnForSlug';
import { buildDabbehDoctor } from '@/modules/dabbeh/functions/buildDabbehDoctor';
import { buildDabbehFullSaman } from '@/modules/dabbeh/functions/buildDabbehFullSaman';
import { useDabbehOnProfile } from '@/modules/dabbeh/rollout';
import { useQueueEnabled } from '@/modules/dabbeh/useQueueEnabled';

const External = dynamic(() => import('./external'), {
  loading(loadingProps) {
    return <Skeleton w="100%" h="198px" rounded="lg" />;
  },
});
const Hamdast = dynamic(() => import('@/modules/hamdast/render').then(m => ({ default: m.Hamdast })), {
  loading() {
    return <Skeleton w="100%" h="4rem" rounded="lg" />;
  },
});

export const Services = ({
  id,
  doctor,
  expertises,
  centers,
  slug,
  onlineVisit,
  waitingTimeInfo,
  profileData,
  isBulk,
  enabledWidgets,
  hamdastWidgetsData,
  dontShowDeactiveBox,
  progressivePending,
  preview,
}: {
  id: string;
  expertises: any;
  doctor: any;
  centers: any[];
  slug: string;
  onlineVisit: any;
  waitingTimeInfo: any;
  profileData: any;
  isBulk: boolean;
  enabledWidgets?: any[];
  hamdastWidgetsData?: Record<string, any>;
  dontShowDeactiveBox?: boolean;
  /** progressive profile shell still loading pieces (analytics wait for the doctor id) */
  progressivePending?: boolean;
  /**
   * Drawn inside the search → profile navigation preview (ProfileNavigationPreview) from prefetched
   * data: no analytics, no GrowthBook writes, no requests of its own, no Saman refresh. The real
   * card mounts on the profile and does all of that exactly as before (docs/performance.md §۳.۱۹).
   */
  preview?: boolean;
}) => {
  const router = useRouter();
  const [servicesRef, inViewServices] = useInView({
    initialInView: true,
  });

  const growthbook = useGrowthBook();
  const checkBookRequestAvailability = useFeatureIsOn('check-book-request-availablility');
  const featureFromHook = useFeatureIsOn('online-visit-chat-lifecycle-duration');
  const [featureFromSlugAttr, setFeatureFromSlugAttr] = useState(false);

  // GB payload listing a slug ≠ feature on. Attribute `slug` must match exactly (e.g. …-3), and
  // features must be ready — otherwise Hami is never called and Plasmic keeps the hardcoded 3-day line.
  useLayoutEffect(() => {
    if (preview) return;
    if (!growthbook || !slug) {
      setFeatureFromSlugAttr(false);
      return;
    }
    growthbook.setAttributes({
      ...growthbook.getAttributes(),
      slug,
    });
    setFeatureFromSlugAttr(growthbook.isOn('online-visit-chat-lifecycle-duration'));
  }, [growthbook, slug, growthbook?.ready, featureFromHook]);

  // The preview may not write the slug attribute (search page); evaluate the slug-targeted flag purely.
  const useChatLifecycleDuration = preview
    ? featureFromHook || isOnForSlug(growthbook, 'online-visit-chat-lifecycle-duration', slug)
    : featureFromHook || featureFromSlugAttr;
  // Profile already has doctor.user_id (information.user_id) in the normal flow → Hami directly.
  // Fall back to profileData.user_id when information omits it; slug only when both missing.
  const profileDoctorUserId = doctor?.user_id ?? profileData?.user_id ?? profileData?.information?.user_id;
  const { days: chatLifecycleDays, isPending: isChatLifecycleDurationPending } =
    useChatLifecycleAutoCloseHours({
      enabled: useChatLifecycleDuration,
      doctorUserId: profileDoctorUserId,
      doctorSlug: profileDoctorUserId ? undefined : slug,
      fallbackSlug: slug,
    });

  const samanQuery = useSamanDoctorAvailabilityStatus(slug, {
    enabled: !!slug && !preview,
  });
  const customize = useCustomize(state => state.customize);
  // «دبّه» rollout: one profile, every visitor (client-side, see modules/dabbeh/rollout). There the card
  // is the full-capacity one — red notice + Dabbeh box — instead of the booking buttons.
  const dabbehRollout = useDabbehOnProfile(slug) && !preview && !customize?.partnerKey;
  // the queue says it is off for this doctor (or does not answer): the real booking card, never a dead end
  const dabbehQueueOn = useQueueEnabled(slug, dabbehRollout);
  const dabbehOn = dabbehRollout && dabbehQueueOn !== false;
  const realReleaseAt = samanQuery.data?.in_person_availability?.nearest_available_time;
  const dabbehFullSaman = useMemo(
    () => (dabbehOn ? buildDabbehFullSaman(centers, realReleaseAt) : null),
    [dabbehOn, centers, realReleaseAt],
  );
  const samanAvailability = dabbehFullSaman ? { ...samanQuery, data: dabbehFullSaman, isError: false } : samanQuery;
  const { isMobile } = useResponsive();
  const isWebView = useWebView();
  const isFloatingNav = useIsNewAppShell();
  const dontShowRateDetails = useFeatureIsOn('ravi_show_external_rate');
  // Owner rollout decision 2026-10-17, phase 1: only doctors with neither in-person nor online
  // booking (the `hasNoBooking && !showOnlineVisit` branch below) — the smallest, lowest-risk
  // population, to measure impact before widening to doctors who still have in-person.
  const showOnlineVisitSuggestionBanner = useFeatureIsOn('online-visit-no-booking-suggestion');
  // Per-profile override (samanBooking/localPrivateSpecialists/config.ts): same-city private
  // specialists replace the online-visit banner and the substitute popup.
  const localSpecialistsConfig = getLocalPrivateSpecialistsConfig(slug);
  const showHamdastGa = useFeatureIsOn('hamdast::ga');
  const { handleOpen: handleOpenAvailabilityModal, modalProps: availabilityModalProps } = useModal();
  const [availabilityMessage, setAvailabilityMessage] = useState<string>('');

  const samanLoaded = !!samanAvailability.data && !samanAvailability.isError;
  const samanPending = !samanLoaded && !samanAvailability.isError;
  const samanStatus: 'pending' | 'success' | 'error' = samanAvailability.isError
    ? 'error'
    : samanAvailability.data
      ? 'success'
      : 'pending';
  // dontShowDeactiveBox means Risman payload exists — hide native Bulk when Risman should own the CTA.
  // Decision source: Saman only (never Full-profile isBulk).
  const showRismanExternalBooking = shouldShowRismanExternalBooking(
    dontShowDeactiveBox ? true : null,
    centers,
    samanAvailability.data,
    samanStatus,
  );
  const scopedInPerson = useMemo(
    () => getScopedInPersonAvailability(centers, samanAvailability.data),
    [centers, samanAvailability.data],
  );
  // «دبّه» box under the release time of the full card (rollout only, see dabbehOn).
  const dabbehEntry = dabbehOn
    ? {
        doctorId: id,
        doctor: buildDabbehDoctor({
          slug,
          doctor,
          expertises,
          centers,
          userId: profileDoctorUserId,
          releaseAt: scopedInPerson.nearestAvailableTime,
        }),
      }
    : undefined;
  const scopedOnlineVisit = useMemo(
    () => getScopedOnlineVisitAvailability(centers, samanAvailability.data),
    [centers, samanAvailability.data],
  );

  // consult_active_booking (onlineVisit.enabled) is legacy — profile UI uses centers API (5532) + Saman.
  const hasPresence =
    router.query.centerTarget != CENTERS.CONSULT && centers.some((center: any) => String(center.id) !== CENTERS.CONSULT);

  const onlineVisitVisibility = getProfileOnlineVisitVisibility({
    centers,
    onlineVisit,
    samanLoaded,
    samanData: samanAvailability.data,
  });
  const showOnlineVisit = onlineVisitVisibility.show;
  const onlineVisitVisibilitySentRef = useRef<string | null>(null);

  useEffect(() => {
    if (!slug || preview) return;
    // Progressive profile: the card can be revealed before the doctor's id arrives; wait for it so
    // the payload carries doctor_id as on a full render.
    if (progressivePending && !id) return;
    // Wait until Saman settled (or errored) so reason is meaningful; still log pending once if never settles.
    if (!samanLoaded && !samanAvailability.isError) return;
    const key = `${slug}:${onlineVisitVisibility.reason}:${showOnlineVisit}`;
    if (onlineVisitVisibilitySentRef.current === key) return;
    onlineVisitVisibilitySentRef.current = key;
    sendDoctorProfileEvent('online_visit_visibility', 'gate_evaluated', {
      slug,
      doctor_id: id,
      show: showOnlineVisit,
      reason: onlineVisitVisibility.reason,
      has_bookable_5532: centers?.some((c: any) => String(c.id) === CENTERS.CONSULT) ?? false,
      saman_loaded: samanLoaded,
      saman_error: samanAvailability.isError,
    });
  }, [
    slug,
    id,
    progressivePending,
    samanLoaded,
    samanAvailability.isError,
    showOnlineVisit,
    onlineVisitVisibility.reason,
    centers,
  ]);

  // Until the doctor's expertise piece lands (progressive shell, navigation preview) the expertise is
  // the search card's, whose groups carry no id: the Plasmic online-visit card then takes its "no
  // group" branch and shows a chat-duration row, removed a moment later when the real expertise
  // (ids) arrives — a 29px jump right after the profile opened. Render the common branch meanwhile;
  // only the duration-row groups (21/47) swap that one line of text when their ids arrive.
  const plasmicProfileData =
    (preview || progressivePending) && !profileData?.expertises?.group_expertises?.length
      ? { ...profileData, expertises: { ...(profileData?.expertises ?? {}), group_expertises: [{ id: null }] } }
      : profileData;

  const consultCenter = centers.find((center: any) => String(center.id) === CENTERS.CONSULT);
  // full-profile service ids can be stale; always load 5532 services from drprofile when showing online visit.
  const needsOnlineServices = showOnlineVisit && !!consultCenter;

  const onlineServicesQuery = useDoctorCenterServices(slug, CENTERS.CONSULT, {
    enabled: !!slug && needsOnlineServices && !preview,
  });

  const onlineVisitServices = useMemo(() => {
    if (!consultCenter) return [];
    const profileServices = filterBookableMappedCenterServices(consultCenter.services ?? []);
    if (!needsOnlineServices) return profileServices;
    if (onlineServicesQuery.isSuccess && onlineServicesQuery.data) {
      // drprofile returns historical inactive consult rows; only show bookable/requestable.
      return filterBookableMappedCenterServices(
        mapDoctorCenterServices(onlineServicesQuery.data, consultCenter.user_center_id),
      );
    }
    if (onlineServicesQuery.isError) return profileServices;
    return [];
  }, [
    consultCenter,
    needsOnlineServices,
    onlineServicesQuery.isSuccess,
    onlineServicesQuery.isError,
    onlineServicesQuery.data,
  ]);
  const canBookPresence = !samanLoaded || scopedInPerson.bookingAvailable;

  const servicesPlacementWidgets = useMemo(
    () => enabledWidgets?.filter((widget: any) => widget?.placement?.includes?.('services')) ?? [],
    [enabledWidgets],
  );

  const renderServicesPlacementWidgets = () =>
    servicesPlacementWidgets.map((widget: any) => (
      <Hamdast
        key={widget.id}
        id={widget.id}
        app={widget?.app}
        backendData={hamdastWidgetsData?.[widget.id] ?? undefined}
        profileData={profileData}
        widgetData={{
          placement: widget?.placement,
          placement_metadata: widget.placements_metadata,
        }}
      />
    ));

  const samanHasNoBooking = samanLoaded && !scopedInPerson.bookingAvailable && !scopedOnlineVisit.bookingAvailable;
  const hasNoBooking = samanAvailability.isError ? true : samanLoaded ? samanHasNoBooking : false;

  const presenceCenters = useMemo(() => {
    const inPersonCenters = centers.filter((center: any) => center.id !== CENTERS.CONSULT);

    if (samanLoaded) {
      return mapSamanAvailabilityToCenters(inPersonCenters, samanAvailability.data).map(center => ({
        ...center,
        waiting_time_info: waitingTimeInfo?.find?.((c: any) => c?.center_id == center.id),
        is_book_request: (center as any).services?.some((service: any) => service.can_request == 1),
      }));
    }

    return inPersonCenters.map(center => ({
      ...center,
      waiting_time_info: waitingTimeInfo?.find?.((c: any) => c?.center_id == center.id),
      is_book_request: center.services?.some((service: any) => service.can_request == 1),
    }));
  }, [centers, samanAvailability.data, samanLoaded, waitingTimeInfo]);

  const onEvent = ({ centerId, serviceId }: { centerId: string; serviceId: string }) => {
    splunkInstance('doctor-profile').sendEvent({
      group: 'booking-widget-doctor-profile',
      type: 'book-button-click',
      event: {
        data: {
          slug: slug,
          center_id: centerId,
          service_id: serviceId,
          features: {
            ravi_show_external_rate: dontShowRateDetails,
          },
          enabled_widgets: enabledWidgets?.map((item: any) => item.id) ?? [],
        },
      },
    });
  };

  const handleOpenBookingPage = async (
    slug: string,
    centerId: string,
    serviceId?: string,
    provider_id?: string,
    user_id?: string,
    city_name?: string,
  ) => {
    const hasValidServiceId = !!serviceId && String(serviceId) !== '-1';
    const isBookRequest =
      hasValidServiceId &&
      centers
        ?.find?.(center => center.id === centerId)
        ?.services?.find?.((service: { id: string }) => service.id === serviceId)?.can_request;

    // Check book request availability if feature flag is enabled and it's a book request
    if (isBookRequest && checkBookRequestAvailability) {
      const userCenterId = centers
        ?.find?.(center => center.id === centerId)
        ?.services?.find?.((service: { id: string }) => service.id === serviceId)?.user_center_id;

      if (userCenterId) {
        try {
          const availabilityResponse = await bookRequestAvailability({
            center_id: centerId,
            user_center_id: userCenterId,
            service_id: serviceId!,
          });

          if (!availabilityResponse.status && availabilityResponse.available_time) {
            // Format ISO time to Jalali readable format
            const formattedTime = moment(availabilityResponse.available_time).locale('fa').calendar(undefined, {
              sameDay: '[امروز] ساعت HH:mm',
              nextDay: '[فردا] ساعت HH:mm',
              sameElse: 'jD jMMMM ساعت HH:mm',
            });

            // Show message to user in modal
            setAvailabilityMessage(`در حال حاضر امکان ثبت درخواست نوبت وجود ندارد. لطفاً ${formattedTime} تلاش کنید.`);
            handleOpenAvailabilityModal();
            return;
          }
        } catch (error) {
          setAvailabilityMessage('در حال حاضر امکان ثبت درخواست نوبت وجود ندارد. لطفاً چند دقیقه دیگر تلاش کنید.');
          handleOpenAvailabilityModal();
          return;
        }
      }
    }

    const params = {
      centerId,
      ...(hasValidServiceId && { serviceId }),
      cityName: city_name,
      ...(provider_id && user_id && { providerId: provider_id, userId: user_id }),
      ...(isBookRequest && { timeId: '-1' }),
    };

    // Navigate first; analytics after — keeps booking click off the Splunk path (INP).
    if (isNativeWebView()) {
      if (isBookRequest) {
        location.assign(`/booking/${slug}?${queryStirng.stringify({ ...params, openInBrowser: 1 })}`);
        onEvent({ centerId, serviceId: serviceId ?? '' });
        return;
      }
    }

    router.push(`/booking/${slug}?${queryStirng.stringify({ ...params })}`);
    onEvent({ centerId, serviceId: serviceId ?? '' });
  };

  if (!customize?.partnerKey && showRismanExternalBooking && hasNoBooking) {
    return servicesPlacementWidgets.length > 0 ? (
      <div className="flex flex-col space-y-3">{renderServicesPlacementWidgets()}</div>
    ) : null;
  }

  if (samanPending) {
    // Same skeleton as the booking section's own pending state (not a plain box), with only the
    // cards this doctor has — so when the reveal gate times out on a slow Saman, nothing changes.
    const hints: BookingHints = { online: showOnlineVisit ? {} : false, presence: hasPresence };
    return (
      <BookingHintsContext.Provider value={hints}>
        <div style={{ height: bookingSkeletonHeight(hints) }}>
          <BookingSkeleton />
        </div>
      </BookingHintsContext.Provider>
    );
  }

  // Legacy / fully unavailable: show only Bulk when nothing is bookable
  if (hasNoBooking && !showOnlineVisit) {
    const sortedAvalaibleTime = () => {
      if (samanLoaded) {
        const futureTimes = [scopedInPerson.nearestAvailableTime, scopedOnlineVisit.nearestAvailableTime].filter(
          (time): time is string => !!time,
        );
        const earliest = futureTimes.sort((a, b) => new Date(a).getTime() - new Date(b).getTime())[0];
        if (earliest) {
          return formatSamanAvailabilityTime(earliest) || undefined;
        }
      }

      return undefined;
    };

    return (
      <div className="flex flex-col space-y-3">
        <BulkService
          displayName={doctor.display_name}
          expertises={expertises}
          availableTime={sortedAvalaibleTime()}
          dcotorCity={doctor?.city_en_slug ?? ''}
          inPersonCity={substituteCity(doctor?.city_en_slug, centers)}
          // Owner rollout 2026-10-17: doctors with neither in-person nor online booking. Owner's
          // call on 2026-10-18 ("جعبه‌ی قدیمی را هم برگردون"): both the old named substitute card
          // and the new unnamed banner render together here — the new banner is left exactly as
          // it was, the old card is just no longer suppressed.
          dabbeh={dabbehEntry}
          showSubstituteDoctor={!localSpecialistsConfig}
        />
        {localSpecialistsConfig ? (
          <LocalPrivateSpecialistSuggestion config={localSpecialistsConfig} ownSlug={slug} />
        ) : (
          showOnlineVisitSuggestionBanner &&
          !dabbehOn && <OnlineVisitSuggestionBanner expertises={expertises} doctorCity={doctor?.city_en_slug} slug={slug} />
        )}
        {renderServicesPlacementWidgets()}
      </div>
    );
  }

  // Hide empty in-person "no booking" box when online visit is active — but keep the future available_time box
  const hidePresenceBecauseOnlineOnly =
    samanLoaded && showOnlineVisit && !scopedInPerson.bookingAvailable && !scopedInPerson.nearestAvailableTime;
  const showPresenceBulk = samanLoaded && hasPresence && !scopedInPerson.bookingAvailable && !hidePresenceBecauseOnlineOnly;
  const showPresenceSection = hasPresence && !hidePresenceBecauseOnlineOnly;

  return (
    <>
      <div ref={servicesRef} className="flex flex-col space-y-3">
        {slug === 'دکتر-پروفسور-نور-بالا' && (
          <External
            title="ویزیت آنلاین (غیر فعال)"
            buttonText="ورود به سایت دکتر پروفسور محمد تقی نوربالا"
            description={`در حال حاضر ویزیت آنلاین این پزشک در پذیرش 24 <strong>غیرفعال</strong> می باشد! شما میتوانید با کلیک بر روی دکمه زیر، از طریق سایت تخصصی این پزشک، نوبت ویزیت آنلاین دریافت کنید.`}
            onBook={() => location.assign('http://drnoorbala.ir/')}
          />
        )}
        {showOnlineVisit && needsOnlineServices && onlineServicesQuery.isFetching && (
          <Skeleton w="100%" h="120px" rounded="lg" />
        )}
        {showOnlineVisit &&
          !(needsOnlineServices && onlineServicesQuery.isFetching) &&
          onlineVisitServices.map((service: any, index: number) => {
            // Feature on (non-21/47): hide Plasmic hardcoded 3-day line while we render Hami hours ourselves.
            // Expertise 21/47 keep legacy "مدت زمان گفت‌وگو" from service.duration — do not touch.
            const expertiseGroupId = expertises?.group_expertises?.[0]?.id;
            const isDurationRowExpertise = expertiseGroupId === 21 || expertiseGroupId === 47;
            const showLifecycleAskDoctorCopy = useChatLifecycleDuration && !isDurationRowExpertise;
            const serviceWithLifecycleDuration = !showLifecycleAskDoctorCopy
              ? service
              : chatLifecycleDays != null
                ? { ...service, chat_lifecycle_auto_close_days: chatLifecycleDays }
                : { ...service, chat_lifecycle_loading: true };

            return (
              <div key={service.id ?? index} className="flex flex-col gap-2">
                <Fragment2
                  name="Services"
                  Component={PlasmicServicesWrapper}
                  args={{
                    ...plasmicProfileData,
                    service: serviceWithLifecycleDuration,
                    onEvent: () => onEvent({ centerId: CENTERS.CONSULT, serviceId: service.id }),
                  }}
                  variants={{ type: 'onlineVisit' }}
                />
                {showLifecycleAskDoctorCopy && isChatLifecycleDurationPending && (
                  <div className="px-4 py-1">
                    <Skeleton w="15rem" h="1rem" rounded="full" />
                  </div>
                )}
              </div>
            );
          })}
        {renderServicesPlacementWidgets()}
        {showPresenceSection &&
          (showPresenceBulk ? (
            <BulkService
              displayName={doctor.display_name}
              expertises={expertises}
              availableTime={formatSamanAvailabilityTime(scopedInPerson.nearestAvailableTime) || undefined}
              dcotorCity={doctor?.city_en_slug ?? ''}
              inPersonCity={substituteCity(doctor?.city_en_slug, centers)}
              showSubstituteDoctor={false}
              dabbeh={dabbehEntry}
            />
          ) : (
            <Presence
              doctorId={id}
              centers={presenceCenters}
              onBook={({ centerId, serviceId }) =>
                handleOpenBookingPage(slug, centerId, serviceId, doctor.provider_id, doctor.user_id, doctor.city_en_slug)
              }
              displayName={doctor.display_name}
              expertises={expertises}
              doctorCity={doctor?.city_en_slug}
              bookingDisabled={samanLoaded && !canBookPresence}
            />
          ))}
        {/* Per-profile override with showWhenBookable: the suggestion stays under the booking card
            while booking is open too (and under the in-person "closed" card when online is open). */}
        {localSpecialistsConfig?.showWhenBookable && showPresenceSection && samanLoaded && (
          <LocalPrivateSpecialistSuggestion
            config={localSpecialistsConfig}
            ownSlug={slug}
            placement={showPresenceBulk ? 'profile' : 'profile-open'}
          />
        )}
      </div>

      {isMobile && !inViewServices && !preview && (
        <div
          // Floating nav: the CTA floats too (same side margins as the pill, no docked white strip).
          className={classNames('fixed z-50 w-full', {
            'p-3 bg-white border-t shadow-card border-slate-100': !isFloatingNav,
            'px-[clamp(12px,calc(20vw_-_52px),24px)] pb-2 pointer-events-none': isFloatingNav,
            'bottom-0': isWebView,
            'bottom-[calc(4rem_+_env(safe-area-inset-bottom))] follow-bottom-nav': !isWebView,
          })}
        >
          <Button
            className={classNames({ 'pointer-events-auto !rounded-full shadow-[0_4px_16px_rgb(0_0_0_/_0.16)]': isFloatingNav })}
            onClick={() => scrollIntoViewWithOffset('#book-me', 90)}
            block
            disabled={samanLoaded && !canBookPresence && !showOnlineVisit}
          >
            دریافت نوبت
          </Button>
        </div>
      )}

      <Modal name="services" title="درخواست نوبت" {...availabilityModalProps} bodyClassName="space-y-3">
        <Text fontWeight="medium" className="leading-7">
          {availabilityMessage}
        </Text>
        <Button block onClick={() => availabilityModalProps.onClose()}>
          متوجه شدم
        </Button>
      </Modal>
    </>
  );
};

export default Services;
